import { toast as sonnerToast, type ExternalToast } from 'sonner';

/**
 * `notify` — a thin tone-aware wrapper around Sonner.
 *
 * Why a wrapper:
 *   - Single source of truth for tone, default duration and microcopy.
 *   - Lets product code call `notify.success('Saved')` without re-importing
 *     sonner everywhere or remembering icon/duration combos.
 *   - Makes it easy to swap the underlying toast lib later without touching
 *     hundreds of call sites.
 *
 * Per Emil + the Sonner authors, durations stay short (3-5s) so toasts feel
 * snappy. Errors stick around a beat longer because the user is more likely
 * to want to read them.
 */
type Tone = 'success' | 'info' | 'warning' | 'error' | 'neutral' | 'loading';

type Options = ExternalToast & { id?: string | number };

function call(tone: Tone, message: string, options?: Options) {
  const opts: Options = {
    duration: tone === 'error' ? 5500 : tone === 'loading' ? Infinity : 3500,
    ...options,
  };
  switch (tone) {
    case 'success':
      return sonnerToast.success(message, opts);
    case 'info':
      return sonnerToast.info(message, opts);
    case 'warning':
      return sonnerToast.warning(message, opts);
    case 'error':
      return sonnerToast.error(message, opts);
    case 'loading':
      return sonnerToast.loading(message, opts);
    default:
      return sonnerToast(message, opts);
  }
}

export const notify = {
  success: (message: string, options?: Options) => call('success', message, options),
  info: (message: string, options?: Options) => call('info', message, options),
  warning: (message: string, options?: Options) => call('warning', message, options),
  error: (message: string, options?: Options) => call('error', message, options),
  loading: (message: string, options?: Options) => call('loading', message, options),
  message: (message: string, options?: Options) => call('neutral', message, options),
  dismiss: (id?: string | number) => sonnerToast.dismiss(id),
  /**
   * Wrap a promise and emit loading → success/error in one call.
   * Mirrors `sonner.toast.promise`.
   */
  promise: <T>(promise: Promise<T>, messages: { loading: string; success: string | ((data: T) => string); error: string | ((err: unknown) => string) }) =>
    sonnerToast.promise(promise, messages),
};

export type { Tone };
